import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Package, CreditCard, Banknote, PiggyBank, Zap, FileSignature, Truck, ChevronRight } from 'lucide-react';
export function FinancingPage() {
  return <div className="min-h-screen bg-white flex flex-col" data-id="element-502">
      <Header data-id="element-503" />

      <main className="flex-grow pt-32 pb-0" data-id="element-504">
        {/* Hero Section */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-20" data-id="element-505">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 uppercase tracking-tight mb-6 leading-tight" data-id="element-506">
            Financing Helps
            <br data-id="element-507" />
            Your
            <br data-id="element-508" />
            Business Grow
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto" data-id="element-509">
            With QuickSpark's simple financing, you can get the equipment you
            need for a low monthly payment you can afford. Quickly earn back
            your investment while maintaining a steady cash flow.
          </p>
          <Button className="bg-red-600 hover:bg-red-700 text-white px-10 py-4 text-lg font-bold uppercase tracking-wide rounded-sm" data-id="element-510">
            Apply Now
          </Button>
        </section>

        {/* ROI Process Flow */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-511">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 md:gap-4 relative" data-id="element-512">
            {/* Step 1 */}
            <div className="flex-1 text-center relative z-10" data-id="element-513">
              <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center" data-id="element-514">
                <Package className="w-16 h-16 text-gray-800" strokeWidth={1.5} data-id="element-515" />
              </div>
              <div className="bg-gray-200 py-2 px-4 mb-4 font-bold text-gray-700 text-sm uppercase tracking-wide" data-id="element-516">
                New Equipment
              </div>
              <p className="text-sm text-gray-600 uppercase font-semibold" data-id="element-517">
                Let's say your new
                <br data-id="element-518" />
                equipment costs
                <br data-id="element-519" />
                <span className="text-red-600" data-id="element-520">$6,000.</span>
              </p>
            </div>

            <ChevronRight className="hidden md:block w-8 h-8 text-red-500 flex-shrink-0" data-id="element-521" />

            {/* Step 2 */}
            <div className="flex-1 text-center relative z-10" data-id="element-522">
              <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center" data-id="element-523">
                <CreditCard className="w-16 h-16 text-gray-800" strokeWidth={1.5} data-id="element-524" />
              </div>
              <div className="bg-gray-200 py-2 px-4 mb-4 font-bold text-gray-700 text-sm uppercase tracking-wide" data-id="element-525">
                Payment
              </div>
              <p className="text-sm text-gray-600 uppercase font-semibold" data-id="element-526">
                Your monthly
                <br data-id="element-527" />
                equipment payment is
                <br data-id="element-528" />
                <span className="text-red-600" data-id="element-529">$225.</span>
              </p>
            </div>

            <ChevronRight className="hidden md:block w-8 h-8 text-red-500 flex-shrink-0" data-id="element-530" />

            {/* Step 3 */}
            <div className="flex-1 text-center relative z-10" data-id="element-531">
              <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center" data-id="element-532">
                <Banknote className="w-16 h-16 text-gray-800" strokeWidth={1.5} data-id="element-533" />
              </div>
              <div className="bg-gray-200 py-2 px-4 mb-4 font-bold text-gray-700 text-sm uppercase tracking-wide" data-id="element-534">
                Earnings
              </div>
              <p className="text-sm text-gray-600 uppercase font-semibold" data-id="element-535">
                You earn <span className="text-red-600" data-id="element-536">$2,500</span> a<br data-id="element-537" />
                month while using
                <br data-id="element-538" />
                the equipment.
              </p>
            </div>

            <ChevronRight className="hidden md:block w-8 h-8 text-red-500 flex-shrink-0" data-id="element-539" />

            {/* Step 4 */}
            <div className="flex-1 text-center relative z-10" data-id="element-540">
              <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center" data-id="element-541">
                <PiggyBank className="w-16 h-16 text-gray-800" strokeWidth={1.5} data-id="element-542" />
              </div>
              <div className="bg-gray-200 py-2 px-4 mb-4 font-bold text-gray-700 text-sm uppercase tracking-wide" data-id="element-543">
                Profits
              </div>
              <p className="text-sm text-gray-600 uppercase font-semibold" data-id="element-544">
                So you pocket
                <br data-id="element-545" />
                <span className="text-red-600" data-id="element-546">$2,275</span> every month!
              </p>
            </div>
          </div>
        </section>

        {/* The Quickspark Advantage */}
        <section className="bg-gray-200 py-20 mb-24" data-id="element-547">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-548">
            <h2 className="text-3xl font-bold text-center text-gray-900 uppercase tracking-tight mb-12" data-id="element-549">
              The Quickspark Advantage
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-550">
              {/* Col 1 */}
              <div className="bg-gray-200 flex flex-col" data-id="element-551">
                <div className="bg-red-600 text-white text-center py-4 px-2 font-bold uppercase tracking-wide min-h-[80px] flex items-center justify-center" data-id="element-552">
                  Generate
                  <br data-id="element-553" />
                  Revenue
                </div>
                <div className="p-6 text-center text-sm text-gray-700 leading-relaxed" data-id="element-554">
                  Financing gives the option to get new equipment now and pay
                  for it in small, manageable payments. You can start earning
                  money with your new equipment before your first payment is
                  due!
                </div>
              </div>
              {/* Col 2 */}
              <div className="bg-gray-200 flex flex-col" data-id="element-555">
                <div className="bg-red-600 text-white text-center py-4 px-2 font-bold uppercase tracking-wide min-h-[80px] flex items-center justify-center" data-id="element-556">
                  Low
                  <br data-id="element-557" />
                  Payments
                </div>
                <div className="p-6 text-center text-sm text-gray-700 leading-relaxed" data-id="element-558">
                  QuickSpark offers monthly payments that range from 12 to 60
                  months and ensures the best fit for your business.
                </div>
              </div>
              {/* Col 3 */}
              <div className="bg-gray-200 flex flex-col" data-id="element-559">
                <div className="bg-red-600 text-white text-center py-4 px-2 font-bold uppercase tracking-wide min-h-[80px] flex items-center justify-center" data-id="element-560">
                  100% Tax
                  <br data-id="element-561" />
                  Deductible
                </div>
                <div className="p-6 text-center text-sm text-gray-700 leading-relaxed" data-id="element-562">
                  Section 179 allows businesses to deduct the full purchase
                  price for the equipment financed. Most businesses are able to
                  deduct 100% of the total equipment costs.
                </div>
              </div>
              {/* Col 4 */}
              <div className="bg-gray-200 flex flex-col" data-id="element-563">
                <div className="bg-red-600 text-white text-center py-4 px-2 font-bold uppercase tracking-wide min-h-[80px] flex items-center justify-center" data-id="element-564">
                  Lines Of
                  <br data-id="element-565" />
                  Credit Open
                </div>
                <div className="p-6 text-center text-sm text-gray-700 leading-relaxed" data-id="element-566">
                  Keep potential lines of credit open for financial emergencies
                  and use our financing to help build your business credentials
                  with any bank.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Who Qualifies */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-24" data-id="element-567">
          <h2 className="text-3xl font-bold text-gray-900 uppercase tracking-tight mb-6" data-id="element-568">
            Who Qualifies?
          </h2>
          <p className="text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed" data-id="element-569">
            QuickSpark partners with multiple funding sources to find the best
            solution for your financial situation. Whether you have a low credit
            score or just starting your business out, we have special financing
            programs for every situation and will get you manageable low monthly
            payments.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto" data-id="element-570">
            <div className="bg-red-600 text-white py-3 px-6 font-bold uppercase tracking-wide rounded-sm shadow-sm" data-id="element-571">
              Startup Business
            </div>
            <div className="bg-red-600 text-white py-3 px-6 font-bold uppercase tracking-wide rounded-sm shadow-sm" data-id="element-572">
              Small/Medium Business
            </div>
            <div className="bg-red-600 text-white py-3 px-6 font-bold uppercase tracking-wide rounded-sm shadow-sm" data-id="element-573">
              Low Credit Score
            </div>
            <div className="bg-red-600 text-white py-3 px-6 font-bold uppercase tracking-wide rounded-sm shadow-sm" data-id="element-574">
              Established Business
            </div>
            <div className="bg-red-600 text-white py-3 px-6 font-bold uppercase tracking-wide rounded-sm shadow-sm" data-id="element-575">
              Bankruptcies
            </div>
            <div className="bg-red-600 text-white py-3 px-6 font-bold uppercase tracking-wide rounded-sm shadow-sm" data-id="element-576">
              High Credit Score
            </div>
          </div>
        </section>

        {/* Simple Application Process */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-24" data-id="element-577">
          <h2 className="text-3xl font-bold text-gray-900 uppercase tracking-tight mb-6" data-id="element-578">
            Simple Application Process
          </h2>
          <p className="text-gray-600 mb-16 max-w-2xl mx-auto leading-relaxed" data-id="element-579">
            With our simple electronic application, you can get an instant
            credit decision within hours of submitting. We can approve financing
            from $1,000 to $100,000.
          </p>

          <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-12 md:gap-4" data-id="element-580">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-2 bg-red-600 z-0" data-id="element-581"></div>
            <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-2 border-t-4 border-dotted border-white z-10" data-id="element-582"></div>

            {/* Step 1 */}
            <div className="flex-1 flex flex-col items-center relative z-20" data-id="element-583">
              <div className="w-24 h-24 rounded-full border-4 border-red-600 bg-white flex items-center justify-center mb-6 shadow-md" data-id="element-584">
                <Zap className="w-10 h-10 text-gray-800" fill="currentColor" data-id="element-585" />
              </div>
              <div className="text-4xl font-bold text-red-600 mb-4" data-id="element-586">1</div>
              <p className="text-xs font-bold text-gray-900 uppercase tracking-wide max-w-[200px]" data-id="element-587">
                Instant credit decision with absolutely no obligations
              </p>
            </div>

            {/* Step 2 */}
            <div className="flex-1 flex flex-col items-center relative z-20" data-id="element-588">
              <div className="w-24 h-24 rounded-full border-4 border-red-600 bg-white flex items-center justify-center mb-6 shadow-md" data-id="element-589">
                <FileSignature className="w-10 h-10 text-gray-800" data-id="element-590" />
              </div>
              <div className="text-4xl font-bold text-red-600 mb-4" data-id="element-591">2</div>
              <p className="text-xs font-bold text-gray-900 uppercase tracking-wide max-w-[200px]" data-id="element-592">
                Electronically sign finance documents
              </p>
            </div>

            {/* Step 3 */}
            <div className="flex-1 flex flex-col items-center relative z-20" data-id="element-593">
              <div className="w-24 h-24 rounded-full border-4 border-red-600 bg-white flex items-center justify-center mb-6 shadow-md" data-id="element-594">
                <Truck className="w-10 h-10 text-gray-800" data-id="element-595" />
              </div>
              <div className="text-4xl font-bold text-red-600 mb-4" data-id="element-596">3</div>
              <p className="text-xs font-bold text-gray-900 uppercase tracking-wide max-w-[200px]" data-id="element-597">
                When the documents are complete, order ships!
              </p>
            </div>
          </div>
        </section>

        {/* Get Started Today */}
        <section className="bg-gray-200 py-20" data-id="element-598">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-599">
            <h2 className="text-3xl font-bold text-gray-900 uppercase tracking-tight mb-6" data-id="element-600">
              Get Started Today
            </h2>
            <div className="w-16 h-1 bg-gray-400 mx-auto mb-8" data-id="element-601"></div>
            <p className="text-gray-700 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-602">
              We strive to deliver a seamless experience so you can focus on
              what matters the most, your bottom line! We've helped thousands of
              applicants ignite their business and are proud to be the trusted
              source in financing solutions.
            </p>
            <Button className="bg-red-600 hover:bg-red-700 text-white px-10 py-4 text-lg font-bold uppercase tracking-wide rounded-sm" data-id="element-603">
              Apply Now
            </Button>
          </div>
        </section>
      </main>

      <Footer data-id="element-604" />
    </div>;
}